import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
import { GoogleSheetsConfig } from './GoogleSheetsConfig';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';
import { UserManagement } from './UserManagement';
import { Roles } from './Roles';
import { Permissions } from './Permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, FileSpreadsheetIcon, MailIcon, UsersIcon, KeyIcon, ShieldCheckIcon } from 'lucide-react';
interface NotificationSettings {
  newAppointments: boolean;
  cancelledAppointments: boolean;
  newPatients: boolean;
}
interface Preferences {
  darkMode: boolean;
  desktopNotifications: boolean;
}
export function Settings() {
  const {
    user,
    updateUser
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaved, setProfileSaved] = useState(false);
  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Preferences state
  const [preferences, setPreferences] = useState<Preferences>({
    darkMode: theme === 'dark',
    desktopNotifications: false
  });
  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newAppointments: true,
    cancelledAppointments: true,
    newPatients: true
  });
  // Load preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem('savidna-preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
      } catch (e) {
        console.error('Error loading preferences:', e);
      }
    }
    const savedNotifs = localStorage.getItem('savidna-notification-settings');
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
      } catch (e) {
        console.error('Error loading notification settings:', e);
      }
    }
  }, []);
  // Save preferences to localStorage
  const savePreferences = (newPrefs: Preferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('savidna-preferences', JSON.stringify(newPrefs));
  };
  // Save notification settings to localStorage
  const saveNotifications = (newNotifs: NotificationSettings) => {
    setNotifications(newNotifs);
    localStorage.setItem('savidna-notification-settings', JSON.stringify(newNotifs));
  };
  const handleSaveProfile = () => {
    if (!name || !email) {
      alert('Por favor completa todos los campos');
      return;
    }
    // Update user in AuthContext
    if (updateUser) {
      updateUser({
        name,
        email
      });
    }
    // Also save to localStorage
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          name,
          email
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        console.error('Error updating user:', e);
      }
    }
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };
  const handleChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    // Update password in localStorage
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const currentUser = users.find((u: any) => u.id === user?.id);
        if (currentUser && currentUser.password !== currentPassword) {
          alert('La contraseña actual es incorrecta');
          return;
        }
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          password: newPassword
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSaved(true);
        setTimeout(() => setPasswordSaved(false), 3000);
      } catch (e) {
        console.error('Error updating password:', e);
        alert('Error al cambiar la contraseña');
      }
    }
  };
  const handleToggleDarkMode = (checked: boolean) => {
    savePreferences({
      ...preferences,
      darkMode: checked
    });
    toggleTheme();
  };
  const handleToggleDesktopNotifications = (checked: boolean) => {
    savePreferences({
      ...preferences,
      desktopNotifications: checked
    });
    if (checked && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Notificaciones activadas', {
            body: 'Ahora recibirás notificaciones de Savidna',
            icon: 'https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg'
          });
        }
      });
    }
  };
  const tabs = [{
    id: 'profile',
    label: 'Perfil',
    icon: <UserIcon className="w-4 h-4" data-id="element-1539" />,
    content: <Card data-id="element-1540">
          <div className="space-y-6" data-id="element-1541">
            <div className="flex items-center gap-6" data-id="element-1542">
              <div className="w-24 h-24 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center" data-id="element-1543">
                <span className="text-3xl font-bold text-sky-600 dark:text-sky-400" data-id="element-1544">
                  {name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div data-id="element-1545">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-1546">
                  {name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400" data-id="element-1547">{email}</p>
                <span className="inline-block mt-2 px-3 py-1 bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 text-sm rounded-full" data-id="element-1548">
                  {user?.role || 'Administrador'}
                </span>
              </div>
            </div>

            {profileSaved && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-1549">
                ✓ Perfil guardado correctamente
              </div>}

            <div className="space-y-4" data-id="element-1550">
              <Input label="Nombre Completo" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" data-id="element-1551" />
              <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" data-id="element-1552" />
              <Input label="Cargo / Rol" value={user?.role || 'Administrador'} disabled data-id="element-1553" />
            </div>

            <Button onClick={handleSaveProfile} data-id="element-1554">Guardar Cambios</Button>
          </div>
        </Card>
  }, {
    id: 'preferences',
    label: 'Preferencias',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1555" />,
    content: <Card data-id="element-1556">
          <div className="space-y-6" data-id="element-1557">
            <div data-id="element-1558">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1559">
                Preferencias del Sistema
              </h3>
              <div className="space-y-4" data-id="element-1560">
                <div className="flex items-center justify-between" data-id="element-1561">
                  <div data-id="element-1562">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1563">
                      Modo Oscuro
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1564">
                      Activar tema oscuro automáticamente
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1565">
                    <input type="checkbox" checked={preferences.darkMode} onChange={e => handleToggleDarkMode(e.target.checked)} className="sr-only peer" data-id="element-1566" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-1567"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-1568">
                  <div data-id="element-1569">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1570">
                      Notificaciones de Escritorio
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1571">
                      Recibir notificaciones en el navegador
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1572">
                    <input type="checkbox" checked={preferences.desktopNotifications} onChange={e => handleToggleDesktopNotifications(e.target.checked)} className="sr-only peer" data-id="element-1573" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-1574"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: <BellIcon className="w-4 h-4" data-id="element-1575" />,
    content: <Card data-id="element-1576">
          <div className="space-y-6" data-id="element-1577">
            <div data-id="element-1578">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1579">
                Configuración de Notificaciones
              </h3>
              <div className="space-y-4" data-id="element-1580">
                <div className="flex items-center justify-between" data-id="element-1581">
                  <div data-id="element-1582">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1583">
                      Nuevas Citas
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1584">
                      Notificar cuando se crea una nueva cita
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1585">
                    <input type="checkbox" checked={notifications.newAppointments} onChange={e => saveNotifications({
                  ...notifications,
                  newAppointments: e.target.checked
                })} className="sr-only peer" data-id="element-1586" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-1587"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-1588">
                  <div data-id="element-1589">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1590">
                      Citas Canceladas
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1591">
                      Notificar cuando se cancela una cita
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1592">
                    <input type="checkbox" checked={notifications.cancelledAppointments} onChange={e => saveNotifications({
                  ...notifications,
                  cancelledAppointments: e.target.checked
                })} className="sr-only peer" data-id="element-1593" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-1594"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-1595">
                  <div data-id="element-1596">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1597">
                      Nuevos Pacientes
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1598">
                      Notificar cuando se registra un nuevo paciente
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-1599">
                    <input type="checkbox" checked={notifications.newPatients} onChange={e => saveNotifications({
                  ...notifications,
                  newPatients: e.target.checked
                })} className="sr-only peer" data-id="element-1600" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-1601"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'security',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-4 h-4" data-id="element-1602" />,
    content: <Card data-id="element-1603">
          <div className="space-y-6" data-id="element-1604">
            <div data-id="element-1605">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1606">
                Seguridad de la Cuenta
              </h3>

              {passwordSaved && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-1607">
                  ✓ Contraseña cambiada correctamente
                </div>}

              <div className="space-y-4" data-id="element-1608">
                <Input label="Contraseña Actual" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-1609" />
                <Input label="Nueva Contraseña" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-1610" />
                <Input label="Confirmar Nueva Contraseña" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-1611" />
                <Button onClick={handleChangePassword} data-id="element-1612">
                  Cambiar Contraseña
                </Button>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'google-sheets',
    label: 'Google Sheets',
    icon: <FileSpreadsheetIcon className="w-4 h-4" data-id="element-1613" />,
    content: <GoogleSheetsConfig data-id="element-1614" />
  }, {
    id: 'email',
    label: 'Correos',
    icon: <MailIcon className="w-4 h-4" data-id="element-1615" />,
    content: <EmailNotificationsConfig data-id="element-1616" />
  }, {
    id: 'users',
    label: 'Usuarios',
    icon: <UsersIcon className="w-4 h-4" data-id="element-1617" />,
    content: <UserManagement data-id="element-1618" />
  }, {
    id: 'permissions',
    label: 'Permisos',
    icon: <KeyIcon className="w-4 h-4" data-id="element-1619" />,
    content: <div className="space-y-6" data-id="element-1620">
          <Roles data-id="element-1621" />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6" data-id="element-1622">
            <Permissions data-id="element-1623" />
          </div>
        </div>
  }];
  return <div className="space-y-6" data-id="element-1624">
      <div data-id="element-1625">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1626">
          Configuración
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1" data-id="element-1627">
          Administra tu cuenta y preferencias
        </p>
      </div>

      <Tabs tabs={tabs} defaultTab="profile" data-id="element-1628" />
    </div>;
}